import { KIT_SPECIFIC_VERSION_LAYOUT } from 'app/router/layouts'
import { latest } from 'app/releases'
import docs from './docs'
import mockups from './mockups'
import moment from 'moment'
import pick from 'lodash/pick'
import { ALL_PLAN_SLUGS } from 'app/views/plans/PlansData.js'
import { loadFullV7 } from 'app/icons/v7Registry'

// These views resolve icons by name at runtime (search results, icon and Kit
// pages), so they load the full $v7 set alongside their own chunk.
const withFullV7 = (loader) => () => Promise.all([loadFullV7(), loader()]).then(([, mod]) => mod)

/**
 * Defines links between site URLs and associated Vue components.
 *
 * Convenience Links:
 * {@link https://v3.router.vuejs.org/guide/#javascript Vue Router Guide}
 */

export default [
  ...docs,
  ...mockups,
  {
    path: '/',
    name: 'home',
    component: () => import('app/views/home/HomeView')
  },
  {
    path: '/changelog',
    name: 'changelog',
    component: () => import('app/views/changelog/ChangeLogView'),
    meta: { version: latest.v7.version }
  },
  {
    path: '/icons/wizard',
    name: 'iconwizard',
    component: () => import('app/views/iconbuilder/IconBuilder')
  },
  {
    path: '/icons/wizard/:token/share',
    name: 'iconwizard.share.show',
    component: () => import('app/views/iconbuilder/IconWizardShareLanding')
  },
  {
    path: '/icons/gallery',
    name: 'icongallery',
    component: () => import('app/views/icongallery/IconGallery')
  },
  {
    path: '/icons/gallery/contest',
    name: 'icongallery.contest',
    component: () => import('app/views/icongallery/IconGalleryContest')
  },
  {
    path: '/sessions/sign-in',
    name: 'sessions.sign-in',
    component: () => import('app/views/sessions/SignInView')
  },
  {
    path: '/sessions/sign-out',
    name: 'sessions.sign-out',
    component: () => import('app/views/sessions/SignOutView')
  },
  {
    path: '/download',
    name: 'latest.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.together.version }
  },
  {
    path: '/account',
    name: 'account',
    redirect: { name: 'account.general' }
  },
  {
    path: '/account/billing',
    name: 'account.billing',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/deleted',
    name: 'account.deleted',
    component: () => import('app/views/accounts/AccountDeletedView')
  },
  {
    path: '/account/general',
    name: 'account.general',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/team',
    name: 'account.team',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/license',
    name: 'account.license',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/settings',
    name: 'account.settings',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/account/tokens',
    name: 'account.tokens',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountView')
  },
  {
    path: '/affiliate',
    name: 'affiliate',
    component: () => import('app/views/affiliate/AffiliateView'),
    redirect: defaultAffiliateSalesRoute,
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    children: [
      {
        path: 'sales',
        name: 'affiliate.sales.default',
        redirect: defaultAffiliateSalesRoute
      },
      {
        path: 'sales/:year/:month',
        name: 'affiliate.sales',
        component: () => import('app/views/affiliate/AffiliateSales')
      },
      {
        path: 'payments',
        name: 'affiliate.payments',
        component: () => import('app/views/affiliate/AffiliatePayments')
      },
      {
        path: 'details',
        name: 'affiliate.details',
        component: () => import('app/views/affiliate/AffiliateDetails')
      }
    ]
  },
  {
    path: '/affiliate/confirm',
    name: 'affiliate.confirm',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/affiliate/AffiliateConfirmView')
  },
  {
    path: '/affiliate/agreement',
    name: 'affiliate.agreement',
    meta: {
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/affiliate/AffiliateAgreementView')
  },
  {
    path: '/account/claim-account',
    name: 'account.claim-account',
    meta: { denyAuth: true },
    component: () => import('app/views/accounts/setup/ClaimAccountView')
  },
  {
    path: '/account/create',
    name: 'account.create',
    component: () => import('app/views/accounts/AccountCreateView')
  },
  {
    path: '/account/cdn',
    name: 'account.cdn',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/AccountCdnView')
  },
  {
    path: '/account/invoices/:id',
    name: 'account.invoice',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/AccountInvoiceView')
  },
  {
    path: '/account/personal-info',
    name: 'account.personal-info',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/setup/PersonalInfoView')
  },
  {
    path: '/account/reactivate',
    name: 'account.reactivate',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/AccountReactivateView')
  },
  {
    path: '/account/seats',
    name: 'account.seats',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/seats/AccountSeatsView')
  },
  {
    // T057 (018-native-plan-changes): the add-seats surface is consolidated
    // into the unified plan-change flow (FR-008). Old deep-links resolve
    // to /plans so the customer enters via the same start-of-flow
    // experience as everyone else — the plan-change view now requires a
    // tier+cycle slug, which only /plans can produce.
    path: '/account/seats/add',
    name: 'account.seats.add',
    meta: { requiresAuth: true },
    redirect: { name: 'plans' }
  },
  {
    // Unified add-ons surface (feature 025-subscription-addons, D1). Hosts the
    // accordion hub: the four add-on resources plus a Seats section that carries
    // the full Manage Seats capability. `?section=` pre-expands the arrived-from
    // section (e.g. ?section=kits from a blocked kit-create). No slug param — the
    // view reads the customer's current subscription for eligibility/plan family.
    path: '/account/add-on',
    name: 'account.add-on',
    meta: { requiresAuth: true },
    component: () => import('app/views/accounts/addOns/AddOnsView')
  },
  {
    // Native Manage Seats flow (feature 019-manage-seats) → subsumed by the
    // unified add-ons surface (025, D1). The Seats section on /account/add-on
    // now carries the full seat-management capability (add + reduce + pending),
    // so this route redirects there with Seats pre-expanded. Nothing in the app
    // links here any more (T077 repointed them all); the redirect stays for
    // bookmarks and external links to /account/manage-seats.
    path: '/account/manage-seats',
    name: 'account.manage-seats',
    meta: { requiresAuth: true },
    redirect: { name: 'account.add-on', query: { section: 'seats' } }
  },
  {
    path: '/account/set-password',
    name: 'account.set-password',
    meta: {
      denyAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/accounts/setup/SetPasswordView')
  },
  {
    path: '/admin',
    meta: {
      requiresAdmin: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/admin/AdminView'),
    children: [
      {
        path: 'accounts',
        name: 'admin.accounts',
        alias: '/admin',
        component: () => import('app/views/admin/accounts/ListView')
      },
      {
        path: 'iconwizardgallery',
        name: 'admin.icongallery',
        component: () => import('app/views/admin/icongallery/IconGalleryAdmin')
      },
      {
        path: 'iconwizardgallery/settings',
        name: 'admin.icongallery.settings',
        component: () => import('app/views/admin/icongallery/IconGalleryAdminSettings')
      },
      {
        path: 'accounts/:id',
        name: 'admin.accounts.detail',
        component: () => import('app/views/admin/accounts/DetailView')
      },
      {
        path: 'accounts/:id/edit',
        name: 'admin.accounts.edit',
        component: () => import('app/views/admin/accounts/EditView')
      },
      {
        path: 'accounts/:accountId/affiliates/create',
        name: 'admin.accounts.affiliates.create',
        component: () => import('app/views/admin/affiliates/CreateView')
      },
      {
        path: 'accounts/:accountId/affiliates/:id',
        name: 'admin.accounts.affiliates.detail',
        component: () => import('app/views/admin/affiliates/DetailView')
      },
      {
        path: 'accounts/:accountId/affiliates/:id/edit',
        name: 'admin.accounts.affiliates.edit',
        component: () => import('app/views/admin/affiliates/EditView')
      },
      {
        path: 'affiliates',
        name: 'admin.affiliates',
        component: () => import('app/views/admin/affiliates/ListView')
      },
      {
        path: 'affiliate-payments',
        name: 'admin.affiliate-payments',
        component: () => import('app/views/admin/affiliates/affiliatePayments/AffiliatePaymentView')
      },
      {
        path: 'affiliate-complete-payments',
        name: 'admin.affiliate-complete-payments',
        component: () => import('app/views/admin/affiliates/affiliatePayments/AffiliateCompletedPaymentsView')
      },
      {
        path: 'accounts/:accountId/invoices/:id',
        name: 'admin.accounts.invoices.detail',
        component: () => import('app/views/admin/accounts/invoices/DetailView')
      },
      {
        path: 'accounts/:accountId/invoices/:id/edit',
        name: 'admin.accounts.invoices.edit',
        component: () => import('app/views/admin/accounts/invoices/EditView')
      },
      {
        path: 'accounts/:accountId/pageviews',
        name: 'admin.accounts.pageviews.detail',
        component: () => import('app/views/admin/accounts/pageviews/DetailView')
      },
      {
        path: 'audits',
        name: 'admin.audits',
        component: () => import('app/views/admin/audits/ListView')
      },
      {
        path: 'braintree-subscriptions',
        name: 'admin.braintree-subscriptions',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/ListView')
      },
      {
        path: 'braintree-subscriptions/:id',
        name: 'admin.braintree-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/DetailView')
      },
      {
        path: 'braintree-subscriptions/:id/edit',
        name: 'admin.braintree-subscriptions.edit',
        component: () => import('app/views/admin/subscriptions/braintreeSubscriptions/EditView')
      },
      {
        path: 'dashboard',
        name: 'admin.dashboard',
        component: () => import('app/views/admin/DashboardView')
      },
      {
        path: 'kits',
        name: 'admin.kits',
        component: () => import('app/views/admin/kits/ListView')
      },
      {
        path: 'kits/:id',
        name: 'admin.kits.detail',
        component: () => import('app/views/admin/kits/DetailView')
      },
      {
        path: 'kits/:id/pageview-domains',
        name: 'admin.kits.pageview.domains',
        component: () => import('app/views/admin/kits/pageviewDomains/PageviewDomainsListView')
      },
      {
        path: 'licenses',
        name: 'admin.licenses',
        component: () => import('app/views/admin/licenses/ListView')
      },
      {
        path: 'paypal-subscriptions',
        name: 'admin.paypal-subscriptions',
        component: () => import('app/views/admin/subscriptions/paypalSubscriptions/ListView')
      },
      {
        path: 'paypal-subscriptions/:id',
        name: 'admin.paypal-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/paypalSubscriptions/DetailView')
      },
      {
        path: 'profiles/:id',
        name: 'admin.profiles.detail',
        component: () => import('app/views/admin/profiles/DetailView')
      },
      {
        path: 'stripe-subscriptions',
        name: 'admin.stripe-subscriptions',
        component: () => import('app/views/admin/subscriptions/stripeSubscriptions/ListView')
      },
      {
        path: 'stripe-subscriptions/:id',
        name: 'admin.stripe-subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/stripeSubscriptions/DetailView')
      },
      {
        path: 'stripe-subscriptions/:id/edit',
        name: 'admin.stripe-subscriptions.edit',
        component: () => import('app/views/admin/subscriptions/stripeSubscriptions/EditView')
      },
      {
        path: 'subscriptions',
        name: 'admin.subscriptions',
        component: () => import('app/views/admin/subscriptions/ListView')
      },
      {
        path: 'subscriptions/:id',
        name: 'admin.subscriptions.detail',
        component: () => import('app/views/admin/subscriptions/DetailView')
      },
      {
        path: 'subscriptions/:id/edit',
        name: 'admin.subscriptions.edit',
        component: () => import('app/views/admin/subscriptions/EditView')
      },
      {
        path: 'tokens/:id',
        name: 'admin.tokens.detail',
        component: () => import('app/views/admin/tokens/DetailView')
      },
      {
        path: 'tools',
        name: 'admin.tools',
        component: () => import('app/views/admin/tools/ListView')
      },
      {
        path: 'tools/iconwizardtest/:icon',
        name: 'admin.tools.iconwizardtest',
        component: () => import('app/views/admin/tools/IconCustomizeTestView')
      },
      {
        path: 'tools/useravatartest',
        name: 'admin.tools.useravatartest',
        component: () => import('app/views/admin/tools/UserAvatarTestView')
      },
      {
        path: 'tools/purgedpendingkitsreport',
        name: 'admin.tools.purgedpendingkitsreport',
        component: () => import('app/views/admin/tools/PurgedPendingKitsReportView')
      },
      {
        path: 'tools/overage-shutdown',
        name: 'admin.tools.overage-shutdown',
        component: () => import('app/views/admin/tools/OverageShutdownView')
      },
      {
        path: 'tools/plan-limits',
        name: 'admin.tools.plan-limits',
        component: () => import('app/views/admin/tools/PlanLimitsView')
      },
      {
        path: 'tools/platform-licenses',
        name: 'admin.tools.platform-licenses',
        component: () => import('app/views/admin/tools/PlatformLicensesView')
      },
      {
        path: 'cloudsmith-analytics',
        name: 'admin.cloudsmith-analytics',
        component: () => import('app/views/admin/cloudsmith/CloudsmithAnalyticsView')
      },
      {
        path: 'quotes',
        name: 'admin.quotes',
        component: () => import('app/views/admin/quotes/CreateView')
      },
      {
        path: 'quotes/invoice',
        name: 'admin.quotes.invoice',
        component: () => import('app/views/admin/quotes/QuoteInvoiceView')
      }
    ]
  },
  {
    path: '/admin/reports',
    name: 'admin.reports',
    meta: {
      requiresAdmin: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/admin/reports/ReportView')
  },
  {
    path: '/checkout/success',
    name: 'checkout.success',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/checkout/SuccessView')
  },
  {
    path: '/kits/:token/icon-upload',
    name: 'kits.kit.icon-upload',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/kits/IconUploadView')
  },
  {
    path: '/kits/:token/image-upload',
    name: 'kits.kit.image-upload',
    meta: {
      requiresAuth: true,
      showBackerServicesShutdownWarning: false
    },
    component: () => import('app/views/kits/ImageUploadView')
  },
  {
    path: '/kits',
    name: 'kits',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitsView')
  },
  {
    path: '/kits/new',
    name: 'kits.new',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitWizard')
  },
  // Account-setup landings for signed-out "Create Your Kit" buyers (spec 2096).
  // Registered before /kits/:token so they aren't read as Kit tokens.
  {
    path: '/kits/from-selection',
    name: 'kits.from-selection',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitFromSelectionView')
  },
  {
    path: '/kits/create-free',
    name: 'kits.create-free',
    meta: { requiresAuth: true },
    component: () => import('app/views/kits/KitCreateFreeView')
  },
  {
    path: '/kits/:token',
    name: 'kits.kit',
    redirect: (to) => {
      return { name: 'kits.kit.icons', params: { token: to.params.token } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/settings',
    name: 'kits.kit.settings',
    redirect: (to) => {
      // Legacy deep-link convention: `#version` used to scroll the Settings
      // page to the version selector. Version is now its own modal, so map
      // `#version` to ?modal=version. Other hashes still target the Settings
      // drawer.
      if (to.hash === '#version') {
        return { name: 'kits.kit.icons', params: { token: to.params.token }, query: { modal: 'version' } }
      }
      return { name: 'kits.kit.icons', params: { token: to.params.token }, query: { drawer: 'settings' }, hash: to.hash || undefined }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/use',
    name: 'kits.kit.use',
    redirect: (to) => {
      return { name: 'kits.kit.icons', params: { token: to.params.token } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/setup',
    name: 'kits.kit.setup',
    redirect: (to) => {
      // Only the setup and notice hints are carried over; KitView acts on them and drops them.
      const query = pick(to.query, ['setup', 'notice'])
      return { name: 'kits.kit.icons', params: { token: to.params.token }, query }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/download',
    name: 'kits.kit.download',
    redirect: (to) => {
      return { name: 'kits.kit.icons', params: { token: to.params.token }, query: { drawer: 'download' } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/package',
    name: 'kits.kit.package',
    redirect: (to) => {
      return { name: 'kits.kit.icons', params: { token: to.params.token }, query: { drawer: 'package' } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/configure',
    name: 'kits.kit.configure',
    redirect: (to) => {
      // Configure tab was retired; old #subset / #icon-selection / #features anchors
      // point back to the Icons tab where the Subsetting modal (and Features in Settings)
      // now live. Preserve the hash so the receiving page can scroll to the matching anchor.
      return { name: 'kits.kit.icons', params: { token: to.params.token }, hash: to.hash || undefined }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/uploaded-icons',
    name: 'kits.kit.uploaded.icons',
    redirect: (to) => {
      return { name: 'kits.kit.customicons', params: { token: to.params.token } }
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/kits/:token/icons/:icon?',
    name: 'kits.kit.icons',
    meta: {
      scrollBehavior: () => {
        return false
      },
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: withFullV7(() => import('app/views/kits/KitView'))
  },
  {
    path: '/kits/:token/customicons/:icon?',
    name: 'kits.kit.customicons',
    meta: {
      scrollBehavior: () => {
        return false
      },
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: withFullV7(() => import('app/views/kits/KitView'))
  },
  {
    path: '/kits/:token/images',
    name: 'kits.kit.images',
    meta: {
      scrollBehavior: () => {
        return false
      },
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: withFullV7(() => import('app/views/kits/KitView'))
  },
  {
    // `:image` is required rather than optional. Router 3 matched
    // `/kits/:token/images` against the route defined first, so the bare
    // URL landed on `kits.kit.images`; Router 4 ranks by specificity
    // instead, and an optional trailing param made this route swallow it.
    // Requiring the param restores the split the URLs always had.
    path: '/kits/:token/images/:image',
    name: 'kits.kit.customImageList',
    meta: {
      requiresAuth: true,
      layout: KIT_SPECIFIC_VERSION_LAYOUT
    },
    component: withFullV7(() => import('app/views/kits/KitView'))
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    redirect: { name: 'leaderboard.new' },
    component: () => import('app/views/community/IconRequestsLeaderboardView'),
    children: [
      {
        path: 'new',
        name: 'leaderboard.new',
        component: () => import('app/views/community/NewIcons')
      },
      {
        path: 'brands',
        name: 'leaderboard.brands',
        component: () => import('app/views/community/BrandIcons')
      },
      {
        path: 'released',
        name: 'leaderboard.released',
        component: () => import('app/views/community/ReleasedIcons')
      },
      {
        path: 'in-progress',
        name: 'leaderboard.inprogress',
        component: () => import('app/views/community/InProgressIcons')
      }
    ]
  },
  {
    path: '/ai',
    name: 'ai',
    component: withFullV7(() => import('app/views/ai/AiView'))
  },
  {
    path: '/license',
    name: 'license.pro',
    component: () => import('app/views/licenses/ProLicenseView')
  },
  {
    path: '/license/free',
    name: 'license.free',
    component: () => import('app/views/licenses/FreeLicenseView')
  },
  {
    path: '/password/forgot',
    name: 'password.forgot',
    meta: { denyAuth: true },
    component: () => import('app/views/passwords/ForgotPasswordView')
  },
  {
    path: '/password/confirm',
    name: 'password.confirm',
    component: () => import('app/views/passwords/ForgotPasswordConfirmView'),
    meta: { denyAuth: true }
  },
  {
    path: '/password/reset',
    name: 'password.reset',
    meta: { denyAuth: true },
    component: () => import('app/views/passwords/ResetPasswordView')
  },
  {
    path: '/search',
    name: 'latest.search',
    component: withFullV7(() => import('app/views/search/IconSearch')),
    meta: { version: latest.v7.version }
  },
  {
    path: '/icons',
    name: 'latest.icons',
    component: () => import('app/views/icons/IconsLanding'),
    meta: {
      scrollBehavior: () => {
        return false
      },
      version: latest.v7.version
    }
  },
  {
    path: '/icons/:family/:style/:icon',
    name: 'latest.icons.detail',
    component: withFullV7(() => import('app/views/icons/IconLanding')),
    meta: { version: latest.v7.version }
  },
  {
    path: '/icons/:icon',
    name: 'latest.icons.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'latest.icons.detail',
        params: { icon: to.params.icon, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/icons/packs',
    name: 'latest.icons.packs',
    redirect: (to) => ({
      name: 'latest.icons',
      query: { t: 'packs' },
      hash: '#packs'
    })
  },
  {
    path: '/icons/packs/:pack',
    name: 'latest.icons.packs.detail',
    component: withFullV7(() => import('app/views/icons/packs/IconPackLanding')),
    meta: { version: latest.v7.version }
  },
  {
    path: '/icons/categories',
    name: 'latest.icons.categories',
    redirect: (to) => ({
      name: 'latest.icons',
      query: { t: 'categories' },
      hash: '#categories'
    })
  },
  {
    path: '/icons/categories/:family/:style/:category',
    name: 'latest.icons.categories.detail',
    component: withFullV7(() => import('app/views/icons/CategoryLanding')),
    meta: { version: latest.v7.version }
  },
  {
    path: '/icons/categories/:category',
    name: 'latest.icons.categories.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'latest.icons.categories.detail',
        params: { category: to.params.category, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/plans',
    name: 'plans',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/plans/PlansView')
  },
  {
    // Public Platform License pricing page. A static path, so Router 4 ranks it
    // above plansRoutes()'s `/plans/:pathMatch(.*)` catch-all, which would
    // otherwise redirect it to /plans and make the page look missing.
    // No `requiresAuth` — procurement reviewers have no account, and publishing
    // the pricing where a buyer can see it is the point of the page.
    path: '/plans/platform',
    name: 'plans.platform',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/plans/PlatformLicenseView')
  },
  {
    // The acceptance flow gets its own focused surface, like the buy pages:
    // limited header with a cancel, simple footer, no marketing furniture.
    // Three of the four people who reach the pricing page cannot use the form
    // (signed out, free plan, wrong role), so keeping it here means those
    // messages answer a deliberate click instead of interrupting a reader.
    path: '/plans/platform/accept',
    name: 'plans.platform.accept',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/plans/PlatformLicenseAcceptView')
  },
  {
    // Native plan-change flow for existing paid customers
    // (feature 018-native-plan-changes). Registered in the static list so it
    // resolves before plansRoutes()'s `/plans/:pathMatch(.*)` catch-all.
    // Free customers and unauthenticated visitors never reach this — they
    // stay on /plans → Checkout (FR-001).
    //
    // `:slug` is required — mirrors the buy flow's `/plans/:slug` pattern.
    // The customer's tier + cycle choice lives in the URL; the view is
    // focused on confirming that choice (seats are the only mutable
    // selection on the page). Bare `/plans/change` won't match this route;
    // the view also redirects back to `/plans` if it ever lands without
    // a usable slug.
    path: '/plans/change/:slug',
    name: 'plans.change',
    // `requiresAuth: true` makes `ensureSignedIn` redirect unauthenticated
    // visitors to `/sessions/sign-in?next=…` before PlanChangeView mounts.
    // Without it, the preview POST returns 403 and the auth-error payload
    // (a non-string `detail` object) was leaking into the inline alert.
    meta: { showBackerServicesShutdownWarning: false, requiresAuth: true },
    component: () => import('app/views/plans/PlanChangeView')
  },
  {
    path: '/privacy',
    name: 'privacy',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/privacy/PrivacyPolicyView')
  },
  {
    path: '/refunds',
    name: 'refunds',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/refunds/RefundsPolicyView')
  },
  {
    path: '/start',
    name: 'start',
    component: () => import('app/views/start/StartView'),
    beforeEnter: (to, from, next) => {
      const { isSignedIn } = to.meta

      if (isSignedIn) {
        window.location.replace('/kits')
      } else {
        next()
      }
    }
  },
  {
    path: '/start/confirm',
    name: 'start.confirm',
    component: () => import('app/views/start/StartConfirmView')
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('app/views/support/SupportView')
  },
  {
    path: '/tos',
    name: 'tos',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/tos/TermsOfServiceView')
  },
  {
    path: '/dpa',
    name: 'dpa',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/dpa/DataProcessingAgreementView')
  },
  {
    path: '/email/preferences/:token?',
    name: 'email.preferences',
    component: () => import('app/views/email/PreferencesView')
  },
  {
    path: '/email/preferences/confirmation/:token?',
    name: 'email.preferences.confirmation',
    component: () => import('app/views/email/ConfirmationView')
  },
  {
    path: '/ux/plancards-wired',
    name: 'ux.plancards-wired',
    meta: { showBackerServicesShutdownWarning: false },
    component: () => import('app/views/ux/PlanCardsWiredView')
  },
  {
    path: '/kickstarter',
    name: 'kickstarter',
    component: () => import('app/views/kickstarter/KickstarterRedirectView')
  },
  {
    path: '/versions',
    name: 'versions',
    component: () => import('app/views/versions/VersionsView')
  },
  {
    // Router 4 removed the `*` catch-all: it must be a param with an explicit
    // regexp. `:pathMatch(.*)*` is the documented equivalent and keeps the
    // route name, so every `{ name: 'not.found' }` navigation still resolves.
    path: '/:pathMatch(.*)*',
    name: 'not.found',
    component: () => import('app/views/errors/NotFoundView')
  },
  // v7 routes
  {
    path: '/v7/icons',
    name: 'seven.icons',
    redirect: { name: 'latest.icons' }
  },
  {
    path: '/v7/changelog',
    name: 'seven.changelog',
    redirect: { name: 'changelog' }
  },
  {
    path: '/v7/download',
    name: 'seven.download',
    redirect: { name: 'latest.download' }
  },
  {
    path: '/v7/icons/:family/:style/:icon',
    name: 'seven.icons.detail',
    redirect: { name: 'latest.icons.detail' }
  },
  {
    path: '/v7/icons/:icon',
    name: 'seven.icons.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'latest.icons.detail',
        params: { icon: to.params.icon, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v7/icons/categories',
    name: 'seven.icons.categories',
    redirect: { name: 'latest.icons.categories' }
  },
  {
    path: '/v7/icons/categories/:family/:style/:category',
    name: 'seven.icons.categories.detail',
    redirect: { name: 'latest.icons.categories.detail' }
  },
  {
    path: '/v7/icons/categories/:category',
    name: 'seven.icons.categories.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'latest.icons.categories.detail',
        params: { category: to.params.category, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v7/icons/packs/',
    name: 'seven.icons.packs',
    redirect: { name: 'latest.icons.packs' }
  },
  {
    path: '/v7/icons/packs/:pack',
    name: 'seven.icons.packs.detail',
    redirect: { name: 'latest.icons.packs.detail' }
  },
  {
    path: '/v7/search',
    name: 'seven.search',
    redirect: { name: 'latest.search' }
  },
  // v6 routes
  {
    path: '/v6/changelog',
    name: 'six.changelog',
    component: () => import('app/views/changelog/V6ChangeLogView'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/download',
    name: 'six.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons',
    name: 'six.icons',
    component: () => import('app/views/icons/IconsLanding'),
    meta: {
      scrollBehavior: () => {
        return false
      },
      version: latest.v6.version
    }
  },
  {
    path: '/v6/icons/:family/:style/:icon',
    name: 'six.icons.detail',
    component: withFullV7(() => import('app/views/icons/IconLanding')),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons/:icon',
    name: 'six.icons.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'six.icons.detail',
        params: { icon: to.params.icon, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v6/icons/categories',
    name: 'six.icons.categories',
    redirect: (to) => ({
      name: 'six.icons',
      query: { t: 'categories' },
      hash: '#categories'
    })
  },
  {
    path: '/v6/icons/categories/:family/:style/:category',
    name: 'six.icons.categories.detail',
    component: withFullV7(() => import('app/views/icons/CategoryLanding')),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/icons/categories/:category',
    name: 'six.icons.categories.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'six.icons.categories.detail',
        params: { category: to.params.category, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v6/icons/packs',
    name: 'six.icons.packs',
    redirect: (to) => ({
      name: 'six.icons',
      query: { t: 'packs' },
      hash: '#packs'
    })
  },
  {
    path: '/v6/icons/packs/:pack',
    name: 'six.icons.packs.detail',
    component: withFullV7(() => import('app/views/icons/packs/IconPackLanding')),
    meta: { version: latest.v6.version }
  },
  {
    path: '/v6/search',
    name: 'six.search',
    component: withFullV7(() => import('app/views/search/IconSearch')),
    meta: { version: latest.v6.version }
  },
  // v5 routes
  {
    path: '/v5/changelog',
    name: 'five.changelog',
    component: () => import('app/views/changelog/V5ChangeLogView'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/download',
    name: 'five.download',
    component: () => import('app/views/download/DownloadView'),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons',
    name: 'five.icons',
    component: () => import('app/views/icons/IconsLanding'),
    meta: {
      scrollBehavior: () => {
        return false
      },
      version: latest.v5.version
    }
  },
  {
    path: '/v5/icons/:family/:style/:icon',
    name: 'five.icons.detail',
    component: withFullV7(() => import('app/views/icons/IconLanding')),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons/:icon',
    name: 'five.icons.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'five.icons.detail',
        params: { icon: to.params.icon, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v5/icons/categories',
    name: 'five.icons.categories',
    redirect: (to) => ({
      name: 'five.icons',
      query: { t: 'categories' },
      hash: '#categories'
    })
  },
  {
    path: '/v5/icons/categories/:family/:style/:category',
    name: 'five.icons.categories.detail',
    component: withFullV7(() => import('app/views/icons/CategoryLanding')),
    meta: { version: latest.v5.version }
  },
  {
    path: '/v5/icons/categories/:category',
    name: 'five.icons.categories.detail.legacy',
    beforeEnter(to, from, next) {
      const { f, s, ...remainingQuery } = to.query
      next({
        name: 'five.icons.categories.detail',
        params: { category: to.params.category, family: f || 'classic', style: s || 'solid' },
        query: remainingQuery,
        replace: true
      })
    }
  },
  {
    path: '/v5/search',
    name: 'five.search',
    component: withFullV7(() => import('app/views/search/IconSearch')),
    meta: {
      scrollBehavior: () => {
        return false
      },
      version: latest.v5.version
    }
  }
].concat(plansRoutes())

function defaultAffiliateSalesRoute(_to) {
  const now = moment()

  return {
    name: 'affiliate.sales',
    params: {
      year: now.format('Y'),
      month: now.format('MM')
    }
  }
}

function plansRoutes() {
  const routes = ALL_PLAN_SLUGS.reduce(
    (acc, slug) =>
      acc.concat({
        path: `/plans/${slug}`,
        name: `plans.${slug}`,
        meta: { showBackerServicesShutdownWarning: false },
        component: () => import('app/views/plans/PlanBuyView')
      }),
    []
  )

  // Catch-all for any /plans/<...> URL that doesn't match a known slug —
  // deprecated tier links from old marketing emails, mistyped URLs, etc.
  // Redirect to the plans page so visitors land somewhere coherent. This
  // self-heals if the slug list ever changes again.
  routes.push({
    path: '/plans/:pathMatch(.*)*',
    redirect: { name: 'plans' }
  })

  return routes
}
